import { useRef, useState } from "react";

/** Where a point sits when nobody has chosen one: the middle, which is what Umbraco crops to. */
export const CENTRE = Object.freeze({ left: 0.5, top: 0.5 });

/** The frames a picture is most often cut to on the site, for when the caller has no better idea. */
const DEFAULT_PREVIEWS = [
  { label: "Wide", aspect: 16 / 9 },
  { label: "Square", aspect: 1 },
  { label: "Tall", aspect: 3 / 4 },
];

const clamp = (value) => Math.min(1, Math.max(0, value));

/** Four places, as the server stores it — well under a pixel on anything a member can upload. */
const round = (value) => Math.round(clamp(value) * 10000) / 10000;

const percent = (value) => `${Math.round(clamp(value) * 1000) / 10}%`;

/** "30% 60%" — for object-position / background-position, the same string the server renders. */
export const toCssPosition = (point) =>
  `${percent(point?.left ?? CENTRE.left)} ${percent(point?.top ?? CENTRE.top)}`;

/** A point from values that may be missing (never set) or strings (read from data- attributes). */
export const toPoint = (left, top) => {
  const l = Number.parseFloat(left);
  const t = Number.parseFloat(top);

  return Number.isFinite(l) && Number.isFinite(t) ? { left: clamp(l), top: clamp(t) } : { ...CENTRE };
};

/**
 * Chooses the part of an image that must stay in frame — the focal point — wherever the image is
 * cut to a different shape: the cover banner, a card, a tile.
 *
 * Nothing is cropped here and nothing is lost: the whole image is kept, and the point is saved
 * with it (the media item's own focal point, which every server-side crop centres on). So the
 * member can come back and change it without uploading again.
 *
 * Three ways to move it, all of which do the same thing:
 *  - click or tap anywhere on the image, or drag the marker (touch included);
 *  - the two sliders under it — real range inputs, so screen readers and keyboards get native
 *    slider semantics. Arrow keys move 1%, Shift + arrow 10%, Home/End go to the edges;
 *  - "Reset to centre".
 *
 * The previews are the image cut to the shapes it will actually be shown at, and follow the point
 * live, so the member sees the result rather than having to imagine it.
 */
export function FocalPointPicker({
  src,
  alt = "",
  value,
  onChange,
  previews = DEFAULT_PREVIEWS,
  copy = {},
  disabled = false,
  testId = "focal-point-picker",
}) {
  const frameRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const point = value ?? CENTRE;
  const c = {
    instructions:
      "Click or drag on the image to choose the part that should always stay in view. It is kept in frame wherever the image is cropped.",
    horizontal: "Horizontal position",
    vertical: "Vertical position",
    reset: "Reset to centre",
    previewHeading: "How it will look",
    ...copy,
  };

  const set = (left, top) => onChange?.({ left: round(left), top: round(top) });

  const pointFromEvent = (event) => {
    const rect = frameRef.current?.getBoundingClientRect();

    if (!rect || rect.width === 0 || rect.height === 0) {
      return null;
    }

    return {
      left: (event.clientX - rect.left) / rect.width,
      top: (event.clientY - rect.top) / rect.height,
    };
  };

  const handlePointerDown = (event) => {
    if (disabled || (event.pointerType === "mouse" && event.button !== 0)) {
      return;
    }

    const next = pointFromEvent(event);

    if (!next) {
      return;
    }

    // Captured so a drag that leaves the image keeps tracking (and clamps at the edge) instead of
    // stopping dead the moment the pointer crosses the border.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
    setIsDragging(true);
    set(next.left, next.top);
  };

  const handlePointerMove = (event) => {
    if (!isDragging) {
      return;
    }

    const next = pointFromEvent(event);

    if (next) {
      set(next.left, next.top);
    }
  };

  const stopDragging = () => setIsDragging(false);

  /**
   * Native range inputs already do 1% per arrow and Home/End; the 10% step with Shift is the one
   * thing they do not, so only that is handled here.
   */
  const handleSliderKeyDown = (axis) => (event) => {
    const direction = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[event.key];

    if (!event.shiftKey || !direction) {
      return;
    }

    event.preventDefault();

    // The same direction the unshifted key moves the range input, just ten times as far.
    const delta = direction * 0.1;

    if (axis === "left") {
      set(point.left + delta, point.top);
    } else {
      set(point.left, point.top + delta);
    }
  };

  const position = toCssPosition(point);
  const hintId = `${testId}-instructions`;

  return (
    <div className="focal-point-picker" data-testid={testId}>
      <p className="focal-point-picker__instructions" id={hintId}>
        {c.instructions}
      </p>

      <div className="focal-point-picker__stage">
        <div
          ref={frameRef}
          className={`focal-point-picker__frame${isDragging ? " focal-point-picker__frame--dragging" : ""}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopDragging}
          onPointerCancel={stopDragging}
          onLostPointerCapture={stopDragging}
          data-testid={`${testId}-image`}
        >
          <img className="focal-point-picker__image" src={src} alt={alt} draggable={false} />

          {/* Decorative: the sliders are the accessible control, so the marker is not announced twice. */}
          <span
            className="focal-point-picker__marker"
            style={{ left: percent(point.left), top: percent(point.top) }}
            aria-hidden="true"
            data-testid={`${testId}-marker`}
          />
        </div>
      </div>

      <div className="focal-point-picker__controls">
        <label className="focal-point-picker__slider">
          <span className="focal-point-picker__slider-label">{c.horizontal}</span>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={Math.round(point.left * 100)}
            onChange={(event) => set(Number(event.target.value) / 100, point.top)}
            onKeyDown={handleSliderKeyDown("left")}
            aria-valuetext={`${percent(point.left)} from the left`}
            aria-describedby={hintId}
            disabled={disabled}
            data-testid={`${testId}-horizontal`}
          />
        </label>

        <label className="focal-point-picker__slider">
          <span className="focal-point-picker__slider-label">{c.vertical}</span>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={Math.round(point.top * 100)}
            onChange={(event) => set(point.left, Number(event.target.value) / 100)}
            onKeyDown={handleSliderKeyDown("top")}
            aria-valuetext={`${percent(point.top)} from the top`}
            aria-describedby={hintId}
            disabled={disabled}
            data-testid={`${testId}-vertical`}
          />
        </label>

        <button
          type="button"
          className="focal-point-picker__reset"
          onClick={() => set(CENTRE.left, CENTRE.top)}
          disabled={disabled}
          data-testid={`${testId}-reset`}
        >
          {c.reset}
        </button>
      </div>

      {previews?.length ? (
        <div className="focal-point-picker__previews">
          <p className="focal-point-picker__previews-heading">{c.previewHeading}</p>

          <ul className="focal-point-picker__preview-list">
            {previews.map((preview) => (
              <li key={preview.label} className="focal-point-picker__preview">
                <span className="focal-point-picker__preview-frame" style={{ aspectRatio: String(preview.aspect) }}>
                  <img src={src} alt="" style={{ objectPosition: position }} data-testid={`${testId}-preview`} />
                </span>
                <span className="focal-point-picker__preview-label">{preview.label}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
